// src/store/slices/escalationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import escalationApi from '../../api/escalationApi';

export const fetchEscalations = createAsyncThunk(
  'escalation/fetchAll',
  async ({ page = 1, pageSize = 10, search = '', employee = '' }, { rejectWithValue }) => {
    try {
      const res = await escalationApi.getAll(page, pageSize, search, employee);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load escalations');
    }
  }
);

export const createEscalation = createAsyncThunk(
  'escalation/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await escalationApi.create(data);
      return res;
    } catch (err) {
      console.log("err", err);
      return rejectWithValue(err.response?.data || 'Failed to create escalation');
    }
  }
);

// Add bulk create thunk
export const bulkCreateEscalations = createAsyncThunk(
  'escalation/bulkCreate',
  async (data, { rejectWithValue }) => {
    try {
      const res = await escalationApi.bulkCreate(data);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to create escalations');
    }
  }
);

export const updateEscalation = createAsyncThunk(
  'escalation/update',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await escalationApi.update(id, data);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to update escalation');
    }
  }
);

export const deleteEscalation = createAsyncThunk(
  'escalation/delete',
  async (id, { rejectWithValue }) => {
    try {
      await escalationApi.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete escalation');
    }
  }
);

const escalationSlice = createSlice({
  name: 'escalation',
  initialState: {
    list: [],
    pagination: { count: 0, page_size: 10, next: null, previous: null },
    isLoading: false,
       isBulkCreating: true,
    error: null,
    searchTerm: '',
    currentPage: 1,
    pageSize: 10,
  },
  reducers: {
    clearError: (state) => { state.error = null; },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    // Fetch All
    builder
      .addCase(fetchEscalations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEscalations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload.data || [];
        state.pagination = action.payload.pagination || { count: 0, page_size: 10 };
      })
      .addCase(fetchEscalations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Create
    builder.addCase(createEscalation.fulfilled, (state, action) => {
      state.list.unshift(action.payload);
    });
  // Bulk Create
     builder
      .addCase(bulkCreateEscalations.pending, (state) => {
        state.isBulkCreating = true;
        state.error = null;
      })
      .addCase(bulkCreateEscalations.fulfilled, (state, action) => {
        state.isBulkCreating = false;
        // Add all the new escalations to the list
        if (Array.isArray(action.payload)) {
          state.list = [...action.payload, ...state.list];
        }
      })
      .addCase(bulkCreateEscalations.rejected, (state, action) => {
        state.isBulkCreating = false;
        state.error = action.payload;
      });

    // Update
    builder.addCase(updateEscalation.fulfilled, (state, action) => {
      const index = state.list.findIndex((e) => e.id === action.payload.id);
      if (index !== -1) state.list[index] = action.payload;
    });

    // Delete
    builder.addCase(deleteEscalation.fulfilled, (state, action) => {
      state.list = state.list.filter((e) => e.id !== action.payload);
    });
  },
});

export const { clearError, setSearchTerm, setCurrentPage, setPageSize } = escalationSlice.actions;
export default escalationSlice.reducer;